import { useEffect, useState } from 'react';
import { Loader2, Plus, SlidersHorizontal, X } from 'lucide-react';
import { analystApi, type MetricAvailability } from '../../services/api';
import { FILTERABLE_METRICS, METRIC_LABEL, type FilterableMetric, type MetricFilterSet, type MetricRange } from '../../shared/analyst/run';
import { describeMetricFilterSet, METRIC_PRESETS, presetRanges, rangeValid } from './metricFilters';

/**
 * Filtri sulle metriche per fonte (Criticita_dev.md, Fase 3, 3.8; decisione D7). Le
 * metriche offerte per ogni fonte sono quelle presenti nei dati, con la loro copertura;
 * per ogni filtro si sceglie se le righe senza valore escono o restano.
 */

interface MetricFiltersPanelProps {
  /** Dataset del perimetro (export o scarico precedente della conversazione); null = non ancora noto. */
  datasetId: string | null;
  value: MetricFilterSet | null;
  onChange: (next: MetricFilterSet | null) => void;
}

const pct = (x: number): string => `${Math.round(x * 100)}%`;
const toNumber = (v: string): number | null => (v.trim() === '' || Number.isNaN(Number(v)) ? null : Math.max(0, Number(v)));

export function MetricFiltersPanel({ datasetId, value, onChange }: MetricFiltersPanelProps) {
  const [open, setOpen] = useState(false);
  const [availability, setAvailability] = useState<MetricAvailability | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!datasetId || !open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    analystApi.metricAvailability(datasetId)
      .then((a) => { if (!cancelled) setAvailability(a); })
      .catch((err: unknown) => { if (!cancelled) setError(err instanceof Error ? err.message : String(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [datasetId, open]);

  const ranges = value?.ranges ?? [];
  const setRanges = (next: MetricRange[], preset: string | null = null): void => onChange(next.length ? { preset, ranges: next } : null);
  const update = (i: number, patch: Partial<MetricRange>): void => setRanges(ranges.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const coverageOf = (source: string, metric: FilterableMetric): number => availability?.sources.find((s) => s.source === source)?.metrics[metric] ?? 0;
  const metricsOf = (source: string): FilterableMetric[] => FILTERABLE_METRICS.filter((m) => coverageOf(source, m) > 0);

  const addRange = (): void => {
    const first = availability?.sources.find((s) => metricsOf(s.source).length > 0);
    if (!first) return;
    const metric = metricsOf(first.source)[0] as FilterableMetric;
    setRanges([...ranges, { sources: [first.source], metric, min: null, max: null, missing: 'exclude' }], null);
  };

  return (
    <div className="border border-gray-800 rounded-lg">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-300 hover:bg-gray-800/60 rounded-lg">
        <SlidersHorizontal className="w-3.5 h-3.5" />
        <span className="font-semibold">Filtri sulle metriche</span>
        <span className="text-gray-500 truncate">{value ? describeMetricFilterSet(value) : 'facoltativi: nessun filtro'}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2 text-xs">
          <p className="text-gray-500">
            Restringono il perimetro a un sottoinsieme (per esempio solo account con seguito): il report lo dichiara e mostra quanto il filtro sposta i risultati.
          </p>
          {!datasetId && <p className="text-gray-400">Disponibili con un export caricato, o per le domande successive su una search già scaricata: le metriche presenti dipendono dalle fonti.</p>}
          {loading && <p className="flex items-center gap-1 text-gray-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Copertura delle metriche…</p>}
          {error && <p className="text-red-300">{error}</p>}
          {availability && (
            <>
              <div className="flex flex-wrap gap-1.5">
                {METRIC_PRESETS.map((p) => {
                  const built = presetRanges(availability, p);
                  return (
                    <button key={p.id} type="button" disabled={built.length === 0} onClick={() => setRanges(built, p.id)}
                      title={built.length ? `Fonti: ${built.map((r) => r.sources[0]).join(', ')}` : 'Nessuna fonte con la metrica in almeno metà delle righe'}
                      className={`px-2.5 py-1 rounded-full border ${value?.preset === p.id ? 'bg-purple-700 border-purple-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500'} disabled:opacity-40`}>
                      {p.label}
                    </button>
                  );
                })}
              </div>
              {ranges.map((r, i) => {
                const source = r.sources[0] ?? '';
                return (
                  <div key={i} className={`flex flex-wrap items-center gap-1.5 ${rangeValid(r) ? '' : 'text-amber-300'}`}>
                    <select value={source} onChange={(e) => update(i, { sources: [e.target.value], metric: metricsOf(e.target.value)[0] ?? r.metric })}
                      className="bg-gray-800 border border-gray-700 rounded px-1.5 py-1 text-white">
                      {availability.sources.filter((s) => metricsOf(s.source).length > 0).map((s) => <option key={s.source} value={s.source}>{s.source} ({s.rows})</option>)}
                    </select>
                    <select value={r.metric} onChange={(e) => update(i, { metric: e.target.value as FilterableMetric })}
                      className="bg-gray-800 border border-gray-700 rounded px-1.5 py-1 text-white">
                      {metricsOf(source).map((m) => <option key={m} value={m}>{METRIC_LABEL[m]} · {pct(coverageOf(source, m))} righe</option>)}
                    </select>
                    <input type="number" min={0} placeholder="min" value={r.min ?? ''} onChange={(e) => update(i, { min: toNumber(e.target.value) })}
                      className="w-24 bg-gray-800 border border-gray-700 rounded px-1.5 py-1 text-white" />
                    <input type="number" min={0} placeholder="max" value={r.max ?? ''} onChange={(e) => update(i, { max: toNumber(e.target.value) })}
                      className="w-24 bg-gray-800 border border-gray-700 rounded px-1.5 py-1 text-white" />
                    <select value={r.missing} onChange={(e) => update(i, { missing: e.target.value as MetricRange['missing'] })}
                      className="bg-gray-800 border border-gray-700 rounded px-1.5 py-1 text-white">
                      <option value="exclude">senza valore: escludi</option>
                      <option value="keep">senza valore: mantieni</option>
                    </select>
                    <button type="button" onClick={() => setRanges(ranges.filter((_, j) => j !== i), null)} className="text-gray-500 hover:text-white" title="Togli il filtro">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
              <div className="flex gap-3">
                <button type="button" onClick={addRange} className="flex items-center gap-1 text-blue-300 hover:underline"><Plus className="w-3.5 h-3.5" /> Aggiungi filtro</button>
                {value && <button type="button" onClick={() => onChange(null)} className="text-gray-400 hover:underline">Togli tutti i filtri</button>}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
